import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSchedule, MdPeople, MdCheckCircle, MdCancel, MdAccessTime, MdPerson, MdLocationOn } from 'react-icons/md';
import { formatTimeRange } from '../utils/dateUtils';
import { normalizeSession, getBatchesArray } from '../utils/sessionUtils';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';

/**
 * Session Card Component
 * Displays session information in a card format
 */
function SessionCard({ session }) {
  const navigate = useNavigate();
  const { user } = useAttendanceAuth();
  const normalizedSession = normalizeSession(session);
  
  // Debug logging
  console.log('SessionCard - Raw session:', session);
  console.log('SessionCard - Normalized session:', normalizedSession);
  console.log('SessionCard - Tutor name:', normalizedSession.tutor_name);

  const getStatusBadge = () => {
    const status = normalizedSession.state?.toLowerCase() || 'pending';
    
    switch (status) {
      case 'marked':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800 flex items-center space-x-1">
            <MdCheckCircle />
            <span>Marked</span>
          </span>
        );
      case 'missed':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800 flex items-center space-x-1">
            <MdCancel />
            <span>Missed</span>
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            Cancelled
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  const handleMarkAttendance = () => {
    // Prevent marking if already marked
    if (normalizedSession.state?.toLowerCase() === 'marked') {
      return;
    }
    if (normalizedSession.can_mark && normalizedSession.id) {
      navigate(`/attendance/tutor/mark/${normalizedSession.id}`);
    }
  };

  const handleViewAttendance = () => {
    if (normalizedSession.can_view && normalizedSession.id) {
      navigate(`/attendance/tutor/view/${normalizedSession.id}`);
    }
  };

  // Check if attendance is already marked
  const isMarked = normalizedSession.state?.toLowerCase() === 'marked';
  const canMark = normalizedSession.can_mark && !isMarked;
  
  const batches = getBatchesArray(normalizedSession.batches);

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-5 sm:p-6 border-2 border-gray-200 hover:border-blue-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 break-words">
            {normalizedSession.subject}
          </h3>
          {batches.length > 0 && (
            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
              <MdPeople className="text-gray-400 flex-shrink-0" />
              <span className="truncate">{batches.join(', ')}</span>
            </div>
          )}
          {normalizedSession.campus_name && (
            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
              <MdLocationOn className="text-gray-400 flex-shrink-0" />
              <span>{normalizedSession.campus_name}</span>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 ml-2">
          {getStatusBadge()}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <MdSchedule className="text-gray-400" />
          <span>
            {normalizedSession.session_date ? new Date(normalizedSession.session_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Date not available'}
          </span>
        </div>

        {normalizedSession.time_start && normalizedSession.time_end && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <MdAccessTime className="text-gray-400" />
            <span>{normalizedSession.time_start} - {normalizedSession.time_end}</span>
          </div>
        )}

        {normalizedSession.period && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Period:</span> {normalizedSession.period}
          </div>
        )}

        {/* Assigned Tutor/Teacher - Always display */}
        <div className="flex items-center space-x-2 text-sm text-gray-700 bg-blue-50 rounded-md p-2 border border-blue-200">
          <MdPerson className="text-blue-500 flex-shrink-0" />
          <span className="font-medium text-blue-900">Teacher:</span>
          <span className="text-blue-800">
            {normalizedSession.tutor_name || user?.name || 'Not Assigned'}
          </span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t-2 border-gray-200">
        {canMark && (
          <button
            onClick={handleMarkAttendance}
            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold text-sm sm:text-base shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
          >
            <MdSchedule />
            <span>Mark Attendance</span>
          </button>
        )}
        {isMarked && normalizedSession.can_view && (
          <button
            onClick={handleViewAttendance}
            className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-semibold text-sm sm:text-base shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
          >
            <MdCheckCircle />
            <span>View Marked Attendance</span>
          </button>
        )}
        {!isMarked && normalizedSession.can_view && (
          <button
            onClick={handleViewAttendance}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold text-sm sm:text-base shadow-md hover:shadow-lg"
          >
            View Attendance
          </button>
        )}
        {!canMark && !normalizedSession.can_view && (
          <span className="text-sm text-gray-500 italic text-center py-2">
            {isMarked ? 'Attendance already marked' : 'No actions available'}
          </span>
        )}
      </div>
    </div>
  );
}

export default SessionCard;

